import type { Broadcast } from '../types';
import { BROADCAST_STATUS_LABEL, AUDIENCE_OPTIONS } from '../types';

interface Props {
  broadcasts: Broadcast[];
  loading: boolean;
  onView: (b: Broadcast) => void;
  onDuplicate: (b: Broadcast) => void;
  onResend: (b: Broadcast) => void;
  onCancel: (id: string) => void;
}

const formatDate = (d: string | null) => {
  if (!d) return '—';
  return new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const channelBadge = (ch: string) => {
  if (ch === 'internal') return { label: 'Interna', icon: 'ri-notification-3-line', cls: 'bg-sky-500/15 text-sky-300' };
  if (ch === 'email') return { label: 'E-mail', icon: 'ri-mail-send-line', cls: 'bg-emerald-500/15 text-emerald-300' };
  return { label: ch, icon: 'ri-question-line', cls: 'bg-white/10 text-white/40' };
};

const audienceLabel = (f: Broadcast['filters']) => {
  const opt = AUDIENCE_OPTIONS.find((o) => o.value === f?.audience);
  if (!opt) return 'Todos';
  if (f.audience === 'inactive') return `Inativos há ${f.inactive_days ?? 30}d`;
  return opt.label;
};

export const BroadcastHistory = ({ broadcasts, loading, onView, onDuplicate, onResend, onCancel }: Props) => {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <i className="ri-loader-4-line text-3xl text-violet-400 animate-spin" />
      </div>
    );
  }

  if (broadcasts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-16 h-16 flex items-center justify-center bg-white/5 rounded-2xl mb-4">
          <i className="ri-megaphone-line text-3xl text-white/20" />
        </div>
        <p className="text-white/40 text-[14px]">Nenhum comunicado enviado ainda</p>
        <p className="text-white/20 text-[12px] mt-1">Crie seu primeiro comunicado na aba "Novo Comunicado"</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {broadcasts.map((b) => {
        const meta = BROADCAST_STATUS_LABEL[b.status];
        return (
          <div key={b.id} className="bg-white/5 border border-white/10 rounded-2xl p-5 hover:border-white/20 transition-all">
            <div className="flex items-start gap-4">
              {/* Status icon */}
              <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${meta.bg}`}>
                <i className={`${meta.icon} ${meta.color} text-[18px]`} />
              </div>

              {/* Main info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <h3 className="text-[15px] font-700 text-white truncate">{b.title}</h3>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-700 ${meta.bg} ${meta.color}`}>
                    {meta.label}
                  </span>
                </div>
                {b.subtitle && <p className="text-white/40 text-[12px] mb-2">{b.subtitle}</p>}

                <div className="flex items-center gap-4 flex-wrap text-[12px] text-white/30">
                  {/* Channels */}
                  <div className="flex items-center gap-1.5">
                    {(b.channels ?? []).map((ch) => {
                      const badge = channelBadge(ch);
                      return (
                        <span key={ch} className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-600 ${badge.cls}`}>
                          <i className={`${badge.icon} text-[10px]`} />{badge.label}
                        </span>
                      );
                    })}
                  </div>

                  {/* Audience */}
                  <span className="flex items-center gap-1">
                    <i className="ri-group-line text-[12px]" />
                    {audienceLabel(b.filters)}
                  </span>

                  {/* Recipients */}
                  {b.total_recipients > 0 && (
                    <span className="flex items-center gap-1">
                      <i className="ri-user-received-2-line text-[12px]" />
                      {b.total_recipients.toLocaleString('pt-BR')} destinatários
                    </span>
                  )}

                  {/* Sent */}
                  {b.sent_at && (
                    <span className="flex items-center gap-1">
                      <i className="ri-time-line text-[12px]" />
                      {formatDate(b.sent_at)}
                    </span>
                  )}
                  {!b.sent_at && b.scheduled_at && (
                    <span className="flex items-center gap-1 text-amber-300">
                      <i className="ri-calendar-schedule-line text-[12px]" />
                      Agendado {formatDate(b.scheduled_at)}
                    </span>
                  )}
                  {!b.sent_at && !b.scheduled_at && (
                    <span className="flex items-center gap-1">
                      <i className="ri-draft-line text-[12px]" />
                      Criado {formatDate(b.created_at)}
                    </span>
                  )}
                </div>

                {/* Stats */}
                {b.status === 'sent' && (
                  <div className="mt-2 flex items-center gap-4 text-[12px]">
                    <span className="text-emerald-400 flex items-center gap-1">
                      <i className="ri-checkbox-circle-line text-[12px]" />
                      {b.sent_count.toLocaleString('pt-BR')} enviados
                    </span>
                    {b.failed_count > 0 && (
                      <span className="text-red-400 flex items-center gap-1">
                        <i className="ri-close-circle-line text-[12px]" />
                        {b.failed_count.toLocaleString('pt-BR')} falhas
                      </span>
                    )}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => onView(b)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 hover:bg-white/10 text-white/40 hover:text-white transition-colors cursor-pointer"
                  title="Visualizar"
                >
                  <i className="ri-eye-line text-[14px]" />
                </button>
                <button
                  type="button"
                  onClick={() => onDuplicate(b)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 hover:bg-white/10 text-white/40 hover:text-white transition-colors cursor-pointer"
                  title="Duplicar"
                >
                  <i className="ri-file-copy-line text-[14px]" />
                </button>
                {b.status === 'sent' && (
                  <button
                    type="button"
                    onClick={() => onResend(b)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 hover:bg-violet-600/30 text-white/40 hover:text-violet-300 transition-colors cursor-pointer"
                    title="Reenviar"
                  >
                    <i className="ri-send-plane-line text-[14px]" />
                  </button>
                )}
                {(b.status === 'draft' || b.status === 'scheduled') && (
                  <button
                    type="button"
                    onClick={() => onCancel(b.id)}
                    className="w-8 h-8 flex items-center justify-center rounded-lg bg-white/5 hover:bg-red-600/20 text-white/40 hover:text-red-400 transition-colors cursor-pointer"
                    title="Cancelar"
                  >
                    <i className="ri-close-line text-[14px]" />
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};
